import { useState } from 'react';
import { Box, Flex, Grid, Text, Tooltip } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import type { TimeslotEntry, TimeslotGroup } from 'api/responseTypes';
import { useCartContext } from 'context/CartContext/CartContext';
import { formatTimeslotDateTime } from 'utils/timeslot';
import { SlotDescriptionBadge } from 'components/SlotDescriptionBadge/SlotDescriptionBadge';

type Props = {
  timeslots: TimeslotGroup[];
  selectedEventKey: string | null;
  onSelect: (slot: TimeslotEntry, group: TimeslotGroup) => void;
};

function isSoldOut(slot: TimeslotEntry): boolean {
  return slot.soldOut || slot.state === 'SOLD_OUT';
}

function getDayNumber(fullDate: string | null): string {
  if (!fullDate) return '';
  return fullDate.split('-')[2] ?? '';
}

function getMonthName(fullDate: string | null): string {
  if (!fullDate) return '';
  try {
    // ar-EG returns the short international Arabic names (يناير، فبراير، مارس… ≤6 chars).
    // ar-IQ returns Iraqi calendar names (كانون الثاني، تشرين الأول…) which exceed badge width.
    return new Date(fullDate + 'T12:00:00').toLocaleString('ar-EG', { month: 'short' });
  } catch {
    return '';
  }
}

function formatGroupDate(fullDate: string | null, fallback: string | null): string {
  if (!fullDate) return fallback ?? '';
  try {
    return new Date(fullDate + 'T12:00:00').toLocaleString('ar-EG', {
      weekday: 'long',
    });
  } catch {
    return fallback ?? '';
  }
}

export const TimeslotSelector = ({ timeslots, selectedEventKey, onSelect }: Props) => {
  const { formatMessage } = useIntl();
  const { tickets } = useCartContext();

  // Use fullDate ?? date as key so null-dated groups each get a unique entry
  const [expandedDates, setExpandedDates] = useState<Set<string | null>>(
    () => new Set(timeslots.filter((g) => g.slots.length === 1).map((g) => g.fullDate ?? g.date)),
  );

  // Lock other timeslots only while the cart has seats.
  // When the user empties the cart the lock releases automatically.
  const isLocked = selectedEventKey !== null && tickets.length > 0;

  const lockedTooltipLabel = formatMessage({
    id: 'eventpage.timeslots.lockedTooltip',
    defaultMessage: 'لاختيار وقت آخر، يُرجى إفراغ السلة أولاً',
  });

  const toggleDate = (fullDate: string | null) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(fullDate)) {
        next.delete(fullDate);
      } else {
        next.add(fullDate);
      }
      return next;
    });
  };

  const selectedGroup = timeslots.find((g) => g.slots.some((s) => s.eventKey === selectedEventKey));
  const selectedSlot = selectedGroup?.slots.find((s) => s.eventKey === selectedEventKey);

  return (
    <Box dir='rtl' borderTop='1px solid' borderBottom='1px solid' borderColor='gray.100' py={8}>
      <Box maxW='1440px' mx='auto' px={6}>
        {/* ── Section header ─────────────────────────────────────────────
            RTL: title → RIGHT (first), subtitle → LEFT (last)             */}
        <Flex justify='start' align='center' mb={6} gap={4}>
          <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight='700'>
            <FormattedMessage id='eventpage.timeslots.title' defaultMessage='اختر اليوم والوقت' />
          </Text>
          <Text fontSize='sm'>
            <span>{timeslots.length} </span>
            <FormattedMessage id='eventpage.timeslots.daysAvailable' defaultMessage='يوم' />
          </Text>
        </Flex>

        {/* ── Selected slot banner ─────────────────────────────────────────
            Figma: gradient bg, #FFA39A border, 10px radius, 8px/12px padding
            RTL: text → RIGHT (first), checkmark → LEFT (last)             */}
        {selectedSlot && selectedGroup && (
          <Flex
            align='center'
            justify='start'
            mb={5}
            px='12px'
            py='8px'
            gap={2}
            style={{ background: 'linear-gradient(135deg, #FFE0DD 0%, #FFF1EF 70.71%)' }}
            border='1px solid'
            borderColor='#FFA39A'
            borderRadius='10px'
          >
            <Flex
              w={8}
              h={8}
              borderRadius='full'
              bg='#FF5C4F'
              align='center'
              justify='center'
              flexShrink={0}
            >
              <CheckIcon size={16} color='white' strokeWidth={4} />
            </Flex>

            {/* Text: RIGHT (first in RTL) */}
            <Box>
              <Text fontSize='xs' color='#921F15' fontWeight='500'>
                <FormattedMessage id='eventpage.timeslots.selected' defaultMessage='اخترت' />
              </Text>
              <Text fontSize='sm' fontWeight='700' color='#1B1917'>
                {formatTimeslotDateTime(
                  selectedSlot.dateAndTime,
                  selectedGroup.date,
                  selectedSlot.time,
                )}
              </Text>
            </Box>
          </Flex>
        )}

        {/* ── Day cards: 2-col desktop, 1-col mobile ──────────────────── */}
        <Grid
          templateColumns={{ base: '1fr', md: `repeat(${Math.min(timeslots.length, 2)}, 1fr)` }}
          gap={4}
          alignItems='start'
        >
          {timeslots.map((group) => {
            // Use fullDate ?? date as the unique key so null-dated groups don't collide
            const dayKey = group.fullDate ?? group.date;
            const isExpanded = expandedDates.has(dayKey);
            const hasSelectedSlot = group.slots.some((s) => s.eventKey === selectedEventKey);
            // This day is locked: a different day's slot is already selected
            const isDayLocked = isLocked && !hasSelectedSlot;
            const availableCount = group.slots.filter((s) => !isSoldOut(s)).length;
            const dayNumber = getDayNumber(group.fullDate);
            const monthName = getMonthName(group.fullDate);

            // ── Single-slot day: the whole row IS the slot ────────────
            if (group.slots.length === 1) {
              const slot = group.slots[0];
              const soldOut = isSoldOut(slot);
              const selected = slot.eventKey === selectedEventKey;
              const isCartLocked = isLocked && !selected;
              const isSlotLocked = isCartLocked || soldOut;

              return (
                <Tooltip
                  key={group.fullDate ?? group.date}
                  isDisabled={!isCartLocked}
                  label={lockedTooltipLabel}
                  hasArrow
                  placement='top'
                >
                  <Flex
                    px={5}
                    py={4}
                    justify='space-between'
                    align='center'
                    borderRadius='2xl'
                    bg={selected ? '#FFF9F8' : 'white'}
                    opacity={isSlotLocked ? 0.55 : 1}
                    cursor={isSlotLocked ? 'not-allowed' : 'pointer'}
                    _hover={isSlotLocked ? {} : { bg: selected ? '#FFE8E5' : 'gray.50' }}
                    onClick={isSlotLocked ? undefined : () => onSelect(slot, group)}
                    transition='background 0.15s, opacity 0.15s'
                    style={{
                      border: selected
                        ? '1.5px solid var(--color-primary-500, #FF5C4F)'
                        : '1.5px solid #E5E7EB',
                    }}
                  >
                    {/* RIGHT (first in RTL): date badge + date + time */}
                    <Flex align='center' gap={3}>
                      <Box
                        bg={selected ? '#FF5C4F' : '#F5F4F2'}
                        borderRadius='9px'
                        minW='36px'
                        h='36px'
                        px={1.5}
                        display='flex'
                        flexDirection='column'
                        alignItems='center'
                        justifyContent='center'
                        gap='1px'
                        flexShrink={0}
                      >
                        <Text
                          fontSize='lg'
                          fontWeight='800'
                          color={selected ? 'white' : '#1B1917'}
                          lineHeight={1}
                        >
                          {dayNumber}
                        </Text>
                        <Text
                          fontSize='9px'
                          color={selected ? 'red.100' : '#79736B'}
                          lineHeight={1}
                        >
                          {monthName}
                        </Text>
                      </Box>
                      <Box minW={0}>
                        <Text
                          fontSize='sm'
                          fontWeight='600'
                          color={selected ? 'red.700' : '#1B1917'}
                        >
                          {formatGroupDate(group.fullDate, group.date)}
                        </Text>
                        <Flex gap={2}>
                          <Text fontSize='xs' color='#79736B' noOfLines={1}>
                            {slot.time}
                          </Text>
                          <Text
                            fontSize='xs'
                            fontWeight='500'
                            color={soldOut ? '#DC2626' : '#15803D'}
                          >
                            {soldOut ? (
                              <FormattedMessage
                                id='eventpage.timeslots.soldOut'
                                defaultMessage='نفذ'
                              />
                            ) : (
                              <FormattedMessage
                                id='eventpage.timeslots.available'
                                defaultMessage='متاح'
                              />
                            )}
                          </Text>
                        </Flex>
                        {slot.slotDescription && (
                          <SlotDescriptionBadge description={slot.slotDescription} mt={1} />
                        )}
                      </Box>
                    </Flex>

                    {/* LEFT (last in RTL): status + radio */}
                    <Flex align='center' gap={2} flexShrink={0}>
                      <Flex
                        w={5}
                        h={5}
                        borderRadius='full'
                        border='2px solid'
                        borderColor={selected ? '#FF5C4F' : 'gray.300'}
                        align='center'
                        justify='center'
                        flexShrink={0}
                      >
                        {selected && <Box w={2.5} h={2.5} borderRadius='full' bg='#FF5C4F' />}
                      </Flex>
                    </Flex>
                  </Flex>
                </Tooltip>
              );
            }

            return (
              // Tooltip wraps each day section; disabled when not locked
              <Tooltip
                key={group.fullDate ?? group.date}
                isDisabled={!isDayLocked}
                label={lockedTooltipLabel}
                hasArrow
                placement='top'
              >
                <Box opacity={isDayLocked ? 0.55 : 1} transition='opacity 0.15s'>
                  {/* ── Day header — standalone component ───────────────
                      Has its own border/bg independent of the slots below.
                      RTL: [badge + date] → RIGHT (first)
                           [count + chevron] → LEFT (last)               */}
                  <Flex
                    px={5}
                    py={4}
                    justify='space-between'
                    align='center'
                    cursor={isDayLocked ? 'not-allowed' : 'pointer'}
                    borderRadius='2xl'
                    bg={hasSelectedSlot ? '#FFF9F8' : 'gray.50'}
                    _hover={isDayLocked ? {} : { bg: hasSelectedSlot ? '#FFE8E5' : 'gray.100' }}
                    onClick={isDayLocked ? undefined : () => toggleDate(dayKey)}
                    transition='background 0.15s'
                    style={{
                      border: hasSelectedSlot
                        ? '1.5px solid var(--color-primary-500, #FF5C4F)'
                        : '1.5px solid #E5E7EB',
                    }}
                  >
                    {/* RIGHT in RTL: date badge + day text */}
                    <Flex align='center' gap={3}>
                      <Box
                        bg={hasSelectedSlot ? '#FF5C4F' : '#F5F4F2'}
                        borderRadius='9px'
                        minW='36px'
                        h='36px'
                        px={1.5}
                        display='flex'
                        flexDirection='column'
                        alignItems='center'
                        justifyContent='center'
                        gap='1px'
                        flexShrink={0}
                      >
                        <Text
                          fontSize='lg'
                          fontWeight='800'
                          color={hasSelectedSlot ? 'white' : '#1B1917'}
                          lineHeight={1}
                        >
                          {dayNumber}
                        </Text>
                        <Text
                          fontSize='9px'
                          color={hasSelectedSlot ? 'red.100' : '#79736B'}
                          lineHeight={1}
                        >
                          {monthName}
                        </Text>
                      </Box>
                      <Text
                        fontSize='sm'
                        fontWeight='600'
                        color={hasSelectedSlot ? 'red.700' : '#79736B'}
                        minW={0}
                      >
                        {formatGroupDate(group.fullDate, group.date)}
                      </Text>
                    </Flex>

                    {/* LEFT in RTL: available count + chevron */}
                    <Flex align='center' gap={2} flexShrink={0}>
                      {/* Status dot + count — coloured availability indicator */}
                      <Flex align='center' gap={1.5}>
                        <Box
                          w={1.5}
                          h={1.5}
                          borderRadius='full'
                          bg={availableCount === 0 ? 'red.500' : 'green.500'}
                          flexShrink={0}
                        />
                        <Text
                          fontSize='xs'
                          fontWeight='500'
                          color={availableCount === 0 ? 'red.600' : 'green.700'}
                        >
                          {availableCount}{' '}
                          <FormattedMessage
                            id='eventpage.timeslots.availableShort'
                            defaultMessage='متاح'
                          />
                        </Text>
                      </Flex>

                      {/* Chevron — rotates when day is expanded */}
                      <ChevronDownIcon
                        size={14}
                        color='#9CA3AF'
                        style={{
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          flexShrink: 0,
                        }}
                      />
                    </Flex>
                  </Flex>

                  {/* ── Expanded: time slots as separate components below ─── */}
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key='slots'
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        <Grid
                          mt={3}
                          templateColumns={{
                            base: '1fr',
                            md: `repeat(${Math.min(group.slots.length, 1)}, 1fr)`,
                          }}
                          gap={3}
                        >
                          {group.slots.map((slot) => {
                            const soldOut = isSoldOut(slot);
                            const selected = slot.eventKey === selectedEventKey;
                            // Slot is locked when another slot is selected and this isn't it
                            const isSlotLocked = isLocked && !selected && !soldOut;

                            return (
                              <Flex
                                key={slot.id}
                                justify='space-between'
                                align='center'
                                px='12px'
                                py='10px'
                                borderRadius='12px'
                                bg={selected ? '#FFF9F8' : 'white'}
                                style={{
                                  border: selected
                                    ? '1.5px solid var(--color-primary-500, #FF5C4F)'
                                    : '1.5px solid #E5E7EB',
                                }}
                                opacity={soldOut || isSlotLocked ? 0.5 : 1}
                                cursor={soldOut || isSlotLocked ? 'not-allowed' : 'pointer'}
                                _hover={
                                  soldOut || isSlotLocked
                                    ? {}
                                    : { bg: selected ? '#FFF9F8' : 'gray.50' }
                                }
                                transition='background 0.15s'
                                onClick={() => !soldOut && !isSlotLocked && onSelect(slot, group)}
                              >
                                <Flex align='start' gap={3}>
                                  <Flex
                                    w={5}
                                    h={5}
                                    borderRadius='full'
                                    border='2px solid'
                                    borderColor={selected ? '#FF5C4F' : 'gray.300'}
                                    align='center'
                                    justify='center'
                                    flexShrink={0}
                                  >
                                    {selected && (
                                      <Box w={2.5} h={2.5} borderRadius='full' bg='#FF5C4F' />
                                    )}
                                  </Flex>

                                  {/* Time + description */}
                                  <Box minW={0}>
                                    <Text
                                      fontSize='sm'
                                      fontWeight={selected ? '600' : '500'}
                                      color={
                                        selected ? 'red.700' : soldOut ? 'gray.400' : 'gray.800'
                                      }
                                      textDecoration={soldOut ? 'line-through' : 'none'}
                                    >
                                      {slot.time}
                                    </Text>
                                    {slot.slotDescription && (
                                      <SlotDescriptionBadge description={slot.slotDescription} />
                                    )}
                                  </Box>
                                </Flex>
                                <Text
                                  fontSize='xs'
                                  fontWeight='500'
                                  color={soldOut ? 'gray.400' : '#15803D'}
                                >
                                  {soldOut ? (
                                    <FormattedMessage
                                      id='eventpage.timeslots.soldOut'
                                      defaultMessage='نفذ'
                                    />
                                  ) : (
                                    <FormattedMessage
                                      id='eventpage.timeslots.available'
                                      defaultMessage='متاح'
                                    />
                                  )}
                                </Text>
                              </Flex>
                            );
                          })}
                        </Grid>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </Tooltip>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};
